import React from "react";
import checkBoxIcon from "@src/assets/svg/checkbox-icon.svg";
import checkboxIndeterminateIcon from "@src/assets/svg/checkbox-indeterminate.svg";
import checkedCheckboxIcon from "@src/assets/svg/checked-checkbox-icon.svg";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import { CleanupConfirmModal } from "@src/components/organisms/modals/cleanup-confirm-modal/cleanup-confirm-modal";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { RenderIfUserHasMultiplePermissions } from "@src/components/molecules/render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { useCleanupViewAdminPanelHook } from "@src/components/organisms/cleanup-view-admin-panel/use-cleanup-view-admin-panel-hook";
import styles from "./cleanup-view-admin-panel.module.scss";

const cssClassName = "cleanup-view-admin-panel";

const CleanupViewAdminPanel = () => {
  const {
    allGatewaysSelected,
    closeConfirmDialog,
    confirmCleanup,
    gateways,
    isLoadingGateways,
    isDeleting,
    openConfirmDialog,
    results,
    selectedGatewayIds,
    selectedGateways,
    showConfirmDialog,
    someGatewaysSelected,
    texts,
    toggleAllGateways,
    toggleGateway,
  } = useCleanupViewAdminPanelHook();

  const serviceUserDeletedText = (
    serviceUserDeleted: boolean | null | undefined,
  ) => {
    if (serviceUserDeleted === true) {
      return texts.serviceUserDeleted;
    }
    if (serviceUserDeleted === false) {
      return texts.serviceUserDeletionFailed;
    }
    return texts.serviceUserNotAttempted;
  };

  const renderIdList = (ids: string[]) =>
    ids.length === 0 ? "0" : `${ids.length} (${ids.join(", ")})`;

  return (
    <FlexColumnContainer>
      <FlexRowContainer justifyContent="space-between">
        <p className={styles[`${cssClassName}__title`]}>{texts.title}</p>
        <RenderIfUserHasMultiplePermissions
          permissions={["cleanup-gateways", "manage-service-gateway-users"]}
          requireAll
        >
          <Button
            type="button"
            text={texts.deleteButton}
            variant="primary"
            disabled={isDeleting || selectedGatewayIds.length === 0}
            fun={openConfirmDialog}
          />
        </RenderIfUserHasMultiplePermissions>
      </FlexRowContainer>
      <p className={styles[`${cssClassName}__description`]}>
        {texts.description}
      </p>
      <div
        aria-busy={isLoadingGateways}
        className={styles[`${cssClassName}__gateway-list`]}
      >
        {isLoadingGateways ? (
          <Spinner />
        ) : gateways.length === 0 ? (
          <div className={styles[`${cssClassName}__gateway-list-empty`]}>
            {texts.noGatewaysFound}
          </div>
        ) : (
          <table className={styles[`${cssClassName}__gateway-table`]}>
            <thead>
              <tr>
                <th scope="col">
                  <label className={styles[`${cssClassName}__checkbox-cell`]}>
                    <input
                      aria-checked={
                        someGatewaysSelected ? "mixed" : allGatewaysSelected
                      }
                      aria-label="Select all gateways"
                      checked={allGatewaysSelected}
                      className={styles[`${cssClassName}__checkbox-input`]}
                      onChange={toggleAllGateways}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = someGatewaysSelected;
                        }
                      }}
                      type="checkbox"
                    />
                    <img
                      alt=""
                      aria-hidden="true"
                      className={styles[`${cssClassName}__checkbox-icon`]}
                      src={
                        allGatewaysSelected
                          ? checkedCheckboxIcon
                          : someGatewaysSelected
                          ? checkboxIndeterminateIcon
                          : checkBoxIcon
                      }
                    />
                  </label>
                </th>
                <th scope="col">{texts.gatewayName}</th>
                <th scope="col">{texts.gatewayId}</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map((gateway) => {
                const isSelected = selectedGatewayIds.includes(
                  gateway.gatewayId,
                );

                return (
                  <tr key={gateway.gatewayId}>
                    <td>
                      <label
                        className={styles[`${cssClassName}__checkbox-cell`]}
                      >
                        <input
                          aria-label={`Select ${gateway.name}`}
                          checked={isSelected}
                          className={styles[`${cssClassName}__checkbox-input`]}
                          onChange={() => toggleGateway(gateway.gatewayId)}
                          type="checkbox"
                        />
                        <img
                          alt=""
                          aria-hidden="true"
                          className={styles[`${cssClassName}__checkbox-icon`]}
                          src={isSelected ? checkedCheckboxIcon : checkBoxIcon}
                        />
                      </label>
                    </td>
                    <td>{gateway.name}</td>
                    <td>{gateway.gatewayId}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {results.length > 0 && (
        <section
          aria-label={texts.resultsTitle}
          className={styles[`${cssClassName}__results`]}
        >
          <p className={styles[`${cssClassName}__results-title`]}>
            {texts.resultsTitle}
          </p>
          <ul className={styles[`${cssClassName}__results-list`]}>
            {results.map((result) => (
              <li
                className={styles[`${cssClassName}__results-item`]}
                data-success={result.success}
                key={result.gatewayId}
              >
                <span className={styles[`${cssClassName}__results-gateway`]}>
                  {result.gatewayName} —{" "}
                  <span
                    className={
                      styles[
                        result.success
                          ? `${cssClassName}__results-status--success`
                          : `${cssClassName}__results-status--failed`
                      ]
                    }
                  >
                    {result.success ? texts.resultSuccess : texts.resultFailed}
                  </span>
                </span>
                {result.success && result.cleanupResponse ? (
                  <span className={styles[`${cssClassName}__results-details`]}>
                    {[
                      {
                        label: texts.namespace,
                        value: result.cleanupResponse.namespace,
                      },
                      {
                        label: texts.gatewayId,
                        value: result.cleanupResponse.gateway_id,
                      },
                      {
                        label: texts.gatewayFound,
                        value: result.cleanupResponse.gateway_found
                          ? texts.yes
                          : texts.no,
                      },
                      {
                        label: texts.deletedClients,
                        value: renderIdList(
                          result.cleanupResponse.deleted_client_ids,
                        ),
                      },
                      {
                        label: texts.deletedDataSources,
                        value: renderIdList(
                          result.cleanupResponse.deleted_data_source_ids,
                        ),
                      },
                      {
                        label: texts.deletedConnectionResults,
                        value: renderIdList(
                          result.cleanupResponse.deleted_connection_result_ids,
                        ),
                      },
                      {
                        label: texts.announcementDeleted,
                        value: result.cleanupResponse.announcement_deleted
                          ? texts.yes
                          : texts.no,
                      },
                    ].map(({ label, value }) => (
                      <span key={label}>
                        {label}: {value}
                      </span>
                    ))}
                    <span>
                      {serviceUserDeletedText(
                        result.cleanupResponse.service_user_deleted,
                      )}
                    </span>
                  </span>
                ) : (
                  <span className={styles[`${cssClassName}__results-error`]}>
                    {result.errorMessage ? `: ${result.errorMessage}` : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {showConfirmDialog && (
        <CleanupConfirmModal
          gateways={selectedGateways}
          isDeleting={isDeleting}
          onClose={closeConfirmDialog}
          onConfirm={confirmCleanup}
        />
      )}
    </FlexColumnContainer>
  );
};

export default CleanupViewAdminPanel;
